import { Callout, DirectionalHint, ICalloutProps } from '@fluentui/react';
import { forwardRef, HTMLAttributes, Ref, useEffect, useRef, useState } from 'react';

import { IComponent, MessageBarListType } from '../../models/components.models';
import { calloutContentStylesNoBorder } from '../../themes/themes';
import { useClassNames, useNotification } from '../../utils/hooks';
import { Button } from '../Button/Button';
import { buttonStylesStealth } from '../Button/Button.styles';
import { IconButton, IIconButtonProps } from '../IconButton/IconButton';
import { iconButtonStylesAppBar } from '../IconButton/IconButton.styles';
import { IMessageBarListProps, MessageBarList } from '../MessageBarList/MessageBarList';
import { messageBarListStylesNotification } from '../MessageBarList/MessageBarList.styles';
import { INotificationClassNames, INotificationStyles, notificationStylesDefault } from './Notification.styles';

export interface INotificationProps extends IComponent<INotificationStyles>, HTMLAttributes<HTMLDivElement> {
  notificationTitle?: string;
  calloutProps?: ICalloutProps;
  iconButtonProps?: IIconButtonProps;
  messageBarListProps?: IMessageBarListProps;
}

export const Notification = forwardRef((props: INotificationProps, ref: Ref<HTMLDivElement>) => {
  const {
    styles,
    className,
    notificationTitle = 'Notifications',
    calloutProps: calloutPropsProp,
    iconButtonProps: iconButtonPropsProp,
    messageBarListProps: messageBarListPropsProp,
    ...elementProps
  } = props;
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const { messages, removeNotification, removeAllNotifications } = useNotification();
  const [refIconButton, setRefIconButton] = useState<HTMLDivElement | null>(null);
  const refMessageCount = useRef(0);
  const iconButtonProps: IIconButtonProps = {
    ...iconButtonPropsProp,
    styles: iconButtonPropsProp?.styles || iconButtonStylesAppBar,
    iconName: iconButtonPropsProp?.iconName || 'Ringer',
    title: 'Notifications',
  };
  const calloutProps: ICalloutProps = {
    ...calloutPropsProp,
    styles: calloutContentStylesNoBorder,
    target: refIconButton,
    directionalHint: DirectionalHint.bottomCenter,
    onDismiss: () => {
      setIsCalloutVisible(false);
    },
  };
  const messageBarListProps: IMessageBarListProps = {
    ...messageBarListPropsProp,
    styles: messageBarListPropsProp?.styles || messageBarListStylesNotification,
    messageBarListType: messageBarListPropsProp?.messageBarListType || MessageBarListType.notification,
    messages,
    onRemoveMessage: (_messageText, message) => removeNotification(message.id),
    onClickMessage: () => setIsCalloutVisible(false),
  };
  const onIconButtonClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (iconButtonPropsProp?.onClick) {
      iconButtonPropsProp.onClick(e);
    }
    setIsCalloutVisible(!isCalloutVisible);
  };
  const classNames = useClassNames<Partial<INotificationStyles>, INotificationClassNames>(
    'Notification',
    notificationStylesDefault,
    styles,
    className,
    messages.length === 0 && 'no-notifications'
  );
  useEffect(() => {
    if (refMessageCount.current < messages.length) {
      setIsCalloutVisible(true);
    }
    refMessageCount.current = messages.length;
  }, [messages]);
  return (
    <div {...elementProps} ref={ref} className={classNames.root} data-test={props['data-test'] || 'notification'}>
      <div ref={setRefIconButton} className={classNames.buttonContainer}>
        <IconButton {...iconButtonProps} onClick={onIconButtonClick} disabled={messages.length === 0} />
        <div className={classNames.count} onClick={onIconButtonClick} title={`${messages.length} notification(s)`}>
          {messages.length}
        </div>
      </div>
      {isCalloutVisible && messages.length > 0 && (
        <Callout {...calloutProps}>
          <div className={classNames.calloutContainer}>
            <div className={classNames.header}>
              <div className={classNames.title}>{notificationTitle}</div>
              <div className={classNames.headerSecondContent}>
                <Button styles={buttonStylesStealth} text="Remove all" onClick={removeAllNotifications} />
              </div>
            </div>
            <MessageBarList {...messageBarListProps} />
          </div>
        </Callout>
      )}
    </div>
  );
});
